import React, { useState } from 'react';
import { 
  Users, 
  Heart, 
  Handshake, 
  Mail, 
  ArrowRight, 
  Target,
  Scale,
  TrendingUp,
  Phone,
  MapPin,
  CheckCircle,
  Mic
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import DonationModal from '../components/DonationModal';
import { 
  stats, 
  impactStories, 
  howToHelpOptions, 
  aboutContent,
  signupNewsletter,
  submitContactForm 
} from '../data/mock';

const Home = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [donationModalOpen, setDonationModalOpen] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  const stories = [
    {
      name: "Natalia",
      story: "She fled with her two children and two bags—everything she owned. She will never see her husband again. Today, she's trying to stand on her feet while facing an impossible question: how to tell her children their father died. With temporary legal status, she can't secure stable housing, access grief counseling, or build the future her family deserves."
    },
    {
      name: "Irina",
      story: "A highly educated woman with a master's degree, she left everything behind. It took her two weeks to reach the Polish border from Mariupol, and she's still struggling to comprehend what she witnessed. Only human understanding and permanent legal pathways can help her heal and rebuild."
    },
    {
      name: "Two Young Sisters",
      story: "Ages 16 and 18, they arrived with no English and no relatives. They want to build their future—to go to school, to college—but they don't know where to start or how to apply. With temporary status, their dreams remain out of reach."
    }
  ];

  // Auto-rotate stories every 8 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStoryIndex((prev) => (prev + 1) % stories.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [stories.length]);

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const result = await signupNewsletter(email, name);
      if (result.success) {
        toast.success(result.message);
        setEmail('');
        setName('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const result = await submitContactForm(contactForm);
      if (result.success) {
        toast.success(result.message);
        setContactForm({ name: '', email: '', message: '' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      mic: Mic,
      users: Users,
      heart: Heart,
      handshake: Handshake
    };
    const Icon = icons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="Svetlana Foundation Logo" 
                className="w-14 h-14 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-emerald-900">Svetlana Foundation</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                About
              </button>
              <button onClick={() => scrollToSection('impact')} className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Impact
              </button>
              <Link to="/resources" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Resources
              </Link>
              <Link to="/book-speaking" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Book Speaking
              </Link>
              <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Contact
              </button>
              <Button 
                onClick={() => setDonationModalOpen(true)}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30"
              >
                Donate
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 opacity-50" />
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold">
                  501(c)(3) Nonprofit Organization
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                No Family Should Live In
                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Legal Uncertainty</span>
              </h1>
              
              {/* Rotating Stories */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100 min-h-[200px] transition-all duration-500">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-5 h-5 text-emerald-600" />
                  <p className="text-sm font-semibold text-emerald-700">
                    Meet {stories[currentStoryIndex].name}
                  </p>
                </div>
                <p className="text-base text-slate-700 leading-relaxed mb-4">
                  {stories[currentStoryIndex].story}
                </p>
                <p className="text-sm font-medium text-slate-900">
                  Your donation helps families like {stories[currentStoryIndex].name}'s secure permanent legal pathways and rebuild their lives.
                </p>
                
                {/* Story navigation dots */}
                <div className="flex gap-2 mt-4 justify-center">
                  {stories.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentStoryIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentStoryIndex 
                          ? 'bg-emerald-600 w-6' 
                          : 'bg-emerald-200 hover:bg-emerald-300'
                      }`}
                      aria-label={`View story ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => setDonationModalOpen(true)}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg px-8 py-6 shadow-xl shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/40 transition-all"
                >
                  Donate Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Link to="/book-speaking">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-emerald-600 hover:bg-emerald-600 bg-white text-emerald-700 hover:text-white text-lg px-8 py-6 w-full"
                  >
                    Book Me to Speak
                    <Mic className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => scrollToSection('about')}
                  className="border-2 border-slate-300 hover:border-emerald-600 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 text-lg px-8 py-6"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-3xl blur-3xl" />
              <img 
                src="https://customer-assets.emergentagent.com/job_advocacy-integration/artifacts/1pflywb0_IMG_9414.jpeg"
                alt="Svetlana Foundation advocacy"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          {/* Recognition Badge */}
          <div className="mb-12 text-center">
            <Card className="inline-block border-none shadow-xl bg-gradient-to-br from-amber-50 to-orange-50 max-w-4xl">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="text-sm font-bold text-amber-800 uppercase tracking-wide mb-1">
                      🏆 Officially Recognized
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                      Connecticut Lieutenant Governor Susan Bysiewicz
                    </h3>
                    <p className="text-slate-700 leading-relaxed">
                      Recognized for <strong>"transforming personal adversity into a mission to serve others"</strong> and dedication to supporting vulnerable Ukrainian families through education, food, and mental health support.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center space-y-2 p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-emerald-50/30 hover:shadow-lg transition-shadow">
                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-lg font-semibold text-slate-900">{stat.label}</div>
                <div className="text-sm text-slate-600">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Mission</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full" />
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_advocacy-integration/artifacts/vpdhnbj0_IMG_9412.jpeg"
                alt="Community advocacy and mobilization"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-900">Transforming Crisis into Opportunity</h3>
              <p className="text-lg text-slate-700 leading-relaxed">
                {aboutContent.mission}
              </p>
              <p className="text-slate-600 leading-relaxed">
                {aboutContent.problem}
              </p>
              <p className="text-slate-600 leading-relaxed">
                {aboutContent.solution}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {aboutContent.approach.map((item, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow bg-white">
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-4">
                    {index === 0 && <Users className="w-6 h-6 text-emerald-600" />}
                    {index === 1 && <Target className="w-6 h-6 text-emerald-600" />}
                    {index === 2 && <TrendingUp className="w-6 h-6 text-emerald-600" />}
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section id="impact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Economic Stability in Action</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real families achieving long-term stability through durable legal pathways
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactStories.map((story) => (
              <Card key={story.id} className="border-none shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 bg-white overflow-hidden group">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={story.image}
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{story.name}</CardTitle>
                  <CardDescription className="text-emerald-600 font-semibold">{story.title}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600 leading-relaxed">{story.story}</p>
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm font-semibold text-slate-900">{story.outcome}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Impact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Your Donation Creates Real Change</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Every dollar helps women and children who fled with nothing rebuild their lives
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Donation Tier Cards */}
            <Card className="border-2 border-emerald-200 shadow-lg hover:shadow-xl transition-all hover:border-emerald-400 bg-white">
              <CardHeader>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-emerald-600">$25</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">
                  Provides English learning materials and resources for families like the two young sisters (16 & 18) who arrived with no English skills
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-300 shadow-lg hover:shadow-xl transition-all hover:border-emerald-500 bg-white">
              <CardHeader>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-emerald-600">$100</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">
                  Connects one family to legal resources, mental health support, and advocacy services to help them heal and rebuild
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-400 shadow-lg hover:shadow-xl transition-all hover:border-emerald-600 bg-white">
              <CardHeader>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-emerald-600">$250</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">
                  Funds a grassroots advocacy event reaching 500+ community members, amplifying voices and building support
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-500 shadow-lg hover:shadow-xl transition-all hover:border-emerald-700 bg-white">
              <CardHeader>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-emerald-600">$500</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">
                  Supports policy meetings with federal lawmakers to advance permanent legal pathways for displaced families
                </p>
              </CardContent>
            </Card>
          </div>

          {/* CTA and Tax Info */}
          <div className="text-center space-y-6">
            <Button 
              size="lg"
              onClick={() => setDonationModalOpen(true)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xl px-12 py-7 shadow-2xl shadow-emerald-500/40 hover:shadow-3xl transition-all"
            >
              Make a Donation Today
              <Heart className="ml-3 w-6 h-6" />
            </Button>
            
            <div className="space-y-2">
              <p className="text-base text-slate-600 flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold">100% tax-deductible</span>
              </p>
              <p className="text-sm text-slate-500">
                The Svetlana Foundation is a registered 501(c)(3) nonprofit. Tax ID: 33-2771387
              </p>
              <p className="text-base text-emerald-700 font-medium mt-4">
                Join 47,400+ supporters making a difference
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Help Section */}
      <section id="help" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How You Can Help</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Join us in building sustainable pathways to economic stability
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {howToHelpOptions.map((option) => (
              <Card key={option.id} className="border-none shadow-lg hover:shadow-xl transition-all bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
                    {getIcon(option.icon)}
                  </div>
                  <CardTitle className="text-xl text-slate-900">{option.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600 leading-relaxed">{option.description}</p>
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                    onClick={() => {
                      if (option.action === 'speaking') window.location.href = '/book-speaking';
                      else if (option.action === 'contact') scrollToSection('contact');
                      else if (option.action === 'donate') setDonationModalOpen(true);
                    }}
                  >
                    {option.action === 'speaking' ? 'Book Now' : option.action === 'signup' ? 'Join Below' : option.action === 'donate' ? 'Donate Now' : 'Contact Us'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Email Signup Form */}
          <div className="max-w-2xl mx-auto">
            <Card className="border-none shadow-2xl bg-white/90 backdrop-blur-md">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-slate-900">Join Our Community</CardTitle>
                <CardDescription className="text-base">
                  Subscribe to receive advocacy updates and ways to make an impact
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEmailSignup} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input 
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-12 border-slate-300 focus:border-emerald-500"
                    />
                    <Input 
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 border-slate-300 focus:border-emerald-500"
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg shadow-lg shadow-emerald-500/30"
                  >
                    {loading ? 'Subscribing...' : 'Subscribe Now'}
                    {!loading && <Mail className="ml-2 w-5 h-5" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-slate-600">
              Questions, partnership inquiries, or want to learn more? Reach out to us.
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                  <a href="mailto:moskvitchsvetlana@gmail.com" className="text-slate-600 hover:text-emerald-600">moskvitchsvetlana@gmail.com</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                  <a href="tel:+14137773136" className="text-slate-600 hover:text-emerald-600">(413) 777-3136</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Location</h3>
                  <p className="text-slate-600">Enfield, Connecticut</p>
                </div>
              </div>
            </div>

            <Card className="border-none shadow-lg bg-slate-50">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <Input 
                    type="text"
                    placeholder="Your name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="h-12 bg-white border-slate-300 focus:border-emerald-500"
                  />
                  <Input 
                    type="email"
                    placeholder="Your email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="h-12 bg-white border-slate-300 focus:border-emerald-500"
                  />
                  <Textarea 
                    placeholder="Your message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="min-h-[150px] bg-white border-slate-300 focus:border-emerald-500"
                  />
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <img 
                  src="/logo.png" 
                  alt="Svetlana Foundation" 
                  className="w-16 h-16 rounded-full object-cover"
                />
                <span className="text-lg font-bold">Svetlana Foundation</span>
              </div>
              <p className="text-slate-400 text-sm">
                Restoring Hope. Rebuilding Lives.
              </p>
              <p className="text-slate-500 text-xs">
                501(c)(3) Nonprofit Organization
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-emerald-400 transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('impact')} className="hover:text-emerald-400 transition-colors">Our Impact</button></li>
                <li><button onClick={() => scrollToSection('help')} className="hover:text-emerald-400 transition-colors">How to Help</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-emerald-400 transition-colors">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Policy Reports</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">News & Updates</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">FAQ</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Facebook</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">LinkedIn</a></li>
                <li><a href="#" className="hover:text-emerald-400 transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-slate-400 text-sm">
            <p>&copy; 2024 Svetlana Foundation. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Donation Modal */}
      <DonationModal isOpen={donationModalOpen} onClose={() => setDonationModalOpen(false)} />
    </div>
  );
};

export default Home;
